import React from 'react'
import PropTypes from 'prop-types'
import { makeStyles, Tooltip } from '@material-ui/core'
import { formatCost } from '../shared/formatCost'
import ScoreRing from './ScoreRing'
import ScoreHistogram from './ScoreHistogram'

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-lg)',
    background: 'linear-gradient(135deg, var(--color-ai-050, #f5f3ff) 0%, white 60%)',
    padding: 'var(--padding-lg)',
    display: 'grid',
    gridTemplateColumns: 'auto 1fr',
    gap: 'var(--gap-lg)',
    alignItems: 'center'
  },
  empty: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white',
    padding: 'var(--padding-md)',
    color: 'var(--color-neutral-500)',
    fontSize: 'var(--text-size-sm)',
    fontStyle: 'italic'
  },
  stats: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  cardTitle: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  statRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 'var(--gap-md)'
  },
  miniStat: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '120px'
  },
  miniStatValue: {
    fontSize: 'var(--text-size-md, 16px)',
    fontWeight: 600,
    color: 'var(--color-neutral-900)'
  },
  miniStatLabel: {
    fontSize: '10px',
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  }
}))

function ScoreCard ({ results, samples, cost }) {
  const classes = useStyles()
  const weighted = results?.aggregate?._weightedScore

  if (!weighted) {
    return (
      <div className={classes.empty}>
        Aggregate score will appear here when judging completes.
      </div>
    )
  }

  const costTip = cost
    ? `Assistant ${formatCost(cost.assistant)} \u00B7 Judge ${formatCost(cost.judge)}`
    : null

  return (
    <div className={classes.card}>
      <ScoreRing value={weighted.mean} />
      <div className={classes.stats}>
        <div className={classes.cardTitle}>Weighted score</div>
        <div className={classes.statRow}>
          <div className={classes.miniStat}>
            <span className={classes.miniStatValue}>{'\u00B1'} {(weighted.stddev * 100).toFixed(1)}%</span>
            <span className={classes.miniStatLabel}>Std dev</span>
          </div>
          <ScoreHistogram samples={samples} />
          <div className={classes.miniStat}>
            <span className={classes.miniStatValue}>{weighted.n}</span>
            <span className={classes.miniStatLabel}>Samples</span>
          </div>
          {cost && (
            <Tooltip title={costTip || ''} arrow disableHoverListener={!costTip}>
              <div className={classes.miniStat}>
                <span className={classes.miniStatValue}>{formatCost(cost.total)}</span>
                <span className={classes.miniStatLabel}>Cost</span>
              </div>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}

ScoreCard.propTypes = {
  results: PropTypes.object,
  samples: PropTypes.array.isRequired,
  cost: PropTypes.object
}

export default ScoreCard
